/* eslint-disable no-shadow */
import { isEmpty, cloneDeep, capitalize } from 'lodash-es';
import { createSelector } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';

import { REWARDS_SLICE_NAME } from 'store/slices/slicesNames';

export const isLoading = (state) => state[REWARDS_SLICE_NAME].isLoading;
export const error = (state) => state[REWARDS_SLICE_NAME].error;
export const campaign = (state) => state[REWARDS_SLICE_NAME].campaign;
export const description = (state) => state[REWARDS_SLICE_NAME].campaign?.description;
export const nounSingular = (state) =>
  state[REWARDS_SLICE_NAME].campaign?.displayNouns?.singular || 'reward';
export const nounPlural = (state) =>
  state[REWARDS_SLICE_NAME].campaign?.displayNouns?.plural || 'rewards';
export const campaignName = (state) => state[REWARDS_SLICE_NAME].campaign?.name;
export const additionalInfoUrl = (state) => state[REWARDS_SLICE_NAME].campaign?.additionalInfoUrl;
export const rewardsMemberHistory = (state) =>
  state[REWARDS_SLICE_NAME].campaign?.rewardsMemberHistory;
export const programImage = (state) => state[REWARDS_SLICE_NAME].campaign?.programImage;

export const hasActiveCampaign = (state) => {
  const { campaign } = state[REWARDS_SLICE_NAME];
  if (typeof campaign !== 'object') return false;
  return !isEmpty(campaign);
};

export const nounSingularCapitalized = createSelector([nounSingular], (noun) => capitalize(noun));
export const nounPluralCapitalized = createSelector([nounPlural], (noun) => capitalize(noun));
export const displayNouns = createSelector([nounSingular, nounPlural], (ns, np) => [ns, np]);
export const displayNounsCapitalized = createSelector(
  [nounSingularCapitalized, nounPluralCapitalized],
  displayNouns.resultFunc
);
export const eligibilityAttributeText = createSelector(
  [nounPluralCapitalized],
  (noun) => `${noun} Eligible`
);

export const bannerText = createSelector(
  [nounPluralCapitalized],
  (noun) => `${noun} are available for eligible services!`
);
export const userIsEligible = (state) => {
  const { campaign } = state[REWARDS_SLICE_NAME];
  if (!campaign) return false;

  const { rewardsMember } = campaign;
  if (!Array.isArray(rewardsMember)) return false;

  return rewardsMember.some((reward) => {
    if (typeof reward.hasRedeemed !== 'boolean' && typeof reward.hasRedeemed !== 'undefined')
      return false;
    return !reward.hasRedeemed;
  });
};

export const allMemberEligibilities = createSelector([campaign], (campaign) => {
  if (campaign === null || isEmpty(campaign)) return [];

  const memberEligibilities = [];
  const clonedCampaign = cloneDeep(campaign);

  clonedCampaign?.rewardsMember?.forEach((re) => {
    re?.memberEligibility?.forEach((eligibility) => {
      eligibility.dollarAmount = re.dollarAmount;
      eligibility.rewardId = re.id;
      if (!eligibility.hasRedeemed) {
        memberEligibilities.push(eligibility);
      }
    });
  });

  return memberEligibilities;
});

export const allRedeemedRewards = createSelector([campaign], (campaign) => {
  if (campaign === null || isEmpty(campaign)) return [];

  const redeemedRewards = [];
  const clonedCampaign = cloneDeep(campaign);

  clonedCampaign?.rewardsMemberHistory?.forEach((re) => {
    re?.memberEligibility?.forEach((eligibility) => {
      eligibility.dollarAmount = re.dollarAmount;
      eligibility.rewardId = re.id;
      if (eligibility.hasRedeemed) {
        redeemedRewards.push(eligibility);
      }
    });
  });

  return redeemedRewards;
});

export const numRewardsAvailable = createSelector(
  [allMemberEligibilities],
  (allMemberEligibilities) => allMemberEligibilities.length
);

export const isClaimsBased = (state) => state[REWARDS_SLICE_NAME].campaign?.claimsBasedEligibility;

export const allRewards = createSelector([campaign], (campaign) => {
  const { rewardsAll = [] } = campaign || {};
  if (!Array.isArray(rewardsAll)) {
    Sentry.captureMessage(`rewardsAll property on ${campaign.name} is not an array`, (scope) => {
      scope.setLevel('warning');
      scope.setFingerprint(['selector:allRewards', campaign.name]);
    });
    return [];
  }

  return [...rewardsAll].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
});

export const allRewardsLength = createSelector([allRewards], (rewards) => rewards.length);

export const member = (state) => state[REWARDS_SLICE_NAME].campaign?.member;

export const memberName = createSelector([member], (member) => {
  if (!member) return undefined;

  const { memberExtra = {} } = member;
  const { firstName, lastName } = memberExtra;

  if (!firstName || !lastName) return undefined;
  return `${firstName} ${lastName}`;
});

export const memberContactValues = createSelector([member], (member) => {
  const result = {};

  result.memberId = member?.memberId || '';
  result.firstName = member?.memberExtra?.firstName || '';
  result.lastName = member?.memberExtra?.lastName || '';
  result.email = member?.memberExtra?.email || member?.memberExtra?.emailAddress || '';

  return result;
});

export const rewardById = (rewardId) =>
  createSelector([allRewards], (allRewards) =>
    allRewards.find((reward) => reward.id === parseInt(rewardId, 10))
  );

export const memberRewardById = (memberRewardId) =>
  createSelector([allMemberEligibilities], (eligibilities) =>
    eligibilities.find((eligibility) => eligibility.memberRewardId === parseInt(memberRewardId, 10))
  );
